import Orion from "@/services/orion";
import Redirect from "@/services/redirect";
import AlreadyOrderedProducts from "@/services/alreadyOrderedProducts";
import { useTrackingStore } from "@/store/tracking";

export default class DuplicateOrderGuard {
  static shouldSkipDuplicateCheck(orderType) {
    return orderType === "upsell";
  }

  static getCartProductIds(cart) {
    const productIds = new Set();

    for (const item of cart?.items || []) {
      const productId =
        item.choice?.salesforceProductId || item.salesforceId || item.id;
      if (productId) {
        productIds.add(String(productId));
      }
    }

    return Array.from(productIds);
  }

  static getCartChoiceIds(cart) {
    const choiceIds = new Set();

    for (const item of cart?.items || []) {
      const choiceId = item.choice?.id || item.choice?.choiceId;
      if (choiceId) {
        choiceIds.add(String(choiceId));
      }
    }

    return Array.from(choiceIds);
  }

  static markCartAsAlreadyOrdered(
    cart,
    extraBlockedChoiceIds = [],
    extraProductIds = [],
  ) {
    const blockedSalesforceIds = new Set([
      ...this.getCartProductIds(cart),
      ...extraProductIds.map((id) => String(id)),
    ]);
    const blockedChoiceIds = new Set([
      ...this.getCartChoiceIds(cart),
      ...extraBlockedChoiceIds.map((id) => String(id)),
    ]);

    const existing = AlreadyOrderedProducts.getAlreadyOrderedProducts() || [];
    let hasChanged = false;

    for (const productSalesforceId of blockedSalesforceIds) {
      if (!existing.includes(productSalesforceId)) {
        existing.push(productSalesforceId);
        hasChanged = true;
      }
    }

    if (hasChanged) {
      AlreadyOrderedProducts.setAlreadyOrderedProducts(existing);
    }

    if (blockedChoiceIds.size || blockedSalesforceIds.size) {
      window.dispatchEvent(
        new CustomEvent("already-ordered-updated", {
          detail: {
            productsSalesforceId: existing,
            blockedChoiceIds: Array.from(blockedChoiceIds),
          },
        }),
      );
    }
  }

  static async isCartDuplicate(cart, order, options = {}) {
    const { insertInRedis = false, useSfId = false } = options;
    const productIds = this.getCartProductIds(cart);

    if (!productIds.length) {
      return false;
    }

    const sfId = useSfId ? useTrackingStore().urlParams?.sfId || "" : "";
    const email =
      !useSfId && order?.billingEmail ? order.billingEmail.toLowerCase() : "";

    if (!sfId && !email) {
      return false;
    }

    for (const productId of productIds) {
      const result = await Orion.getAlreadyOrdered(
        productId,
        sfId,
        email,
        insertInRedis,
      );
      if (result?.status) {
        return true;
      }
    }

    return false;
  }

  static handleDuplicateBlocked(cart, options = {}) {
    const {
      reason = "duplicate",
      blockedChoiceIds = [],
      blockedProductIds = [],
    } = options;

    this.markCartAsAlreadyOrdered(cart, blockedChoiceIds, blockedProductIds);
    Redirect.redirectToAlreadySubscribed({ reason });
  }

  static async blockIfDuplicate(cart, order, options = {}) {
    const { orderType, insertInRedis = false, useSfId = false } = options;

    if (this.shouldSkipDuplicateCheck(orderType)) {
      return false;
    }

    try {
      const isDuplicate = await this.isCartDuplicate(cart, order, {
        insertInRedis,
        useSfId,
      });

      if (!isDuplicate) {
        return false;
      }

      this.handleDuplicateBlocked(cart, options);
      return true;
    } catch (error) {
      console.error("[DuplicateOrderGuard] duplicate check failed", error);
      return false;
    }
  }
}
